import { SettingsService } from '../../../../../services/settings.service';
import {
  Component,
  DoCheck,
  Input,
  OnInit,
  ViewChild,
  TemplateRef,
  OnDestroy,
} from '@angular/core';
import { QuoteService } from 'src/app/services/quote.service';
import { MatTable } from '@angular/material/table';
import { MatDialog } from '@angular/material/dialog';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { ConfQuote, ConfQuoteLine, Field } from 'src/app/model';
import { EntityService } from 'src/app/services/entity.service';
import { QrcodeScannerComponent } from '../../../../qrcode-scanner/qrcode-scanner.component';
import { LayoutService } from '../../../../../services/layout.service';
import { ParentProductService } from 'src/app/services/parentproduct.service';
import { ChildProductsDialogComponent } from 'src/app/common/child-products-dialog/child-products-dialog.component';
import { take } from 'rxjs/operators';
import { ProductListHelper } from '../../../../helper';
import { ProductListService } from '../../../../../services/product-list.service';
import { ErpWarehouseService } from '../../../../../services/erp/erp-warehouse.service';

@Component({
  selector: 'app-productlist-field',
  templateUrl: './productlist-field.component.html',
  styleUrls: ['./productlist-field.component.css'],
})
export class ProductlistFieldComponent implements OnInit, DoCheck, OnDestroy {
  @ViewChild(MatTable) table: MatTable<any>;
  @ViewChild('childProductsDialog') childProductsDialog: TemplateRef<any>;

  @Input() ITEM: any;
  @Input() ITEMID: number | string;
  @Input() disabled = false;
  @Input() allAttr: any;
  @Input() entCode: string;

  displayedColumns: string[] = ProductListHelper.fetchAllDisplayColumns();
  selectedColumns: string[] = [];
  localstorageKey: string;
  confQuote = new ConfQuote();
  gridCols = this.productListService.makeGridColumns();
  product_fields: Field[] = [];
  productFieldsForScanner: Field[] = [];
  selectedWarehouseId: number | null = null;
  disableCustomPrice =
    this.settingsService.getSavedSettings('quoteConfDisableCustomPrice')[0]
      ?.value === 'true';
  warehouses = this.erpWarehouseService.productListWarehouses;
  isHandset = this.layoutService.isHandset;

  constructor(
    private readonly quoteService: QuoteService,
    public readonly dialog: MatDialog,
    public readonly settingsService: SettingsService,
    private readonly entityService: EntityService,
    private readonly layoutService: LayoutService,
    private readonly parentProductService: ParentProductService,
    private readonly productListService: ProductListService,
    private readonly erpWarehouseService: ErpWarehouseService
  ) {}

  ngOnInit(): void {
    this.setLocalStorageKeyValue();
    this.refreshColumnsOnLocalStorage();
    this.getProductFields();
    this.copyOldLocalStorageItemsToNewOnes();
    this.erpWarehouseService.getWarehouses().add(() => this.fillTableLines());
    this.productListService.checkForAddressField(this.allAttr);
  }

  ngDoCheck(): void {
    this.productListService.checkForAddressField(this.allAttr);
  }

  ngOnDestroy(): void {
    this.productListService.resetAddressData();
    this.parentProductService.setCheckboxState(false);
  }

  rememberSelectedCols() {
    this.selectedColumns = this.productListService.fetchSelectedColumns(
      this.localstorageKey,
      this.ITEM,
      this.displayedColumns,
      this.product_fields
    );
    this.table.renderRows();
  }

  private getSavedLines(quoteId: number) {
    this.quoteService
      .getSavedConf(quoteId)
      .pipe(take(1))
      .subscribe((res) => {
        if (this.ITEM.fld_code_ent === 'pickingorder_prodlist') {
          res = this.productListService.sortProductLinesBasedOnWarehouses(res);
        }
        this.ITEM.prodlines = res;
        this.confQuote.quotelines = res;
        this.addItemLine(null);
        this.refresh();
      });
  }

  addItemLine(item: any) {
    const newLine = this.productListService.createNewLine(
      item,
      this.confQuote,
      this.selectedWarehouseId
    );
    this.confQuote.quotelines.push(newLine);
    this.refresh();
  }

  removeItemLine(index: number) {
    this.confQuote.quotelines.splice(index, 1);
    this.reorderProducts();
    this.refresh();
  }

  refresh() {
    this.checkForContainerRounding();
    this.productListService.calculateTotalPrice(this.confQuote);
    this.productListService.updateTotalValueEntityField(
      this.allAttr,
      this.confQuote.totalAmount
    );
    this.ITEM.prodlines = this.confQuote.quotelines.filter((x) => x.product);
    this.table.renderRows();
  }

  changeUnit(line: ConfQuoteLine) {
    this.productListService.changeUnit(line);
    this.productListService.calculateTotalPrice(this.confQuote);
  }

  drop(event: CdkDragDrop<string[]>) {
    moveItemInArray(
      this.selectedColumns,
      event.previousIndex,
      event.currentIndex
    );
    this.productListService.storeSelectedColumnsToLocalstorage(
      this.selectedColumns,
      this.localstorageKey
    );
  }

  upItemLine(index: number) {
    moveItemInArray(this.confQuote.quotelines, index - 1, index);
    this.confQuote.quotelines = this.confQuote.quotelines.slice();
    this.reorderProducts();
  }

  downItemLine(index: number) {
    moveItemInArray(this.confQuote.quotelines, index + 1, index);
    this.confQuote.quotelines = this.confQuote.quotelines.slice();
    this.reorderProducts();
  }

  fillProdlineItem(item: any, line: ConfQuoteLine): void {
    const customCheckboxState = this.parentProductService.getCheckboxState();
    if (customCheckboxState) {
      ChildProductsDialogComponent.openChildProductsDialog(
        this.dialog,
        item.id,
        this.confQuote.quotelines.length,
        this.confQuote.totalDiscount,
        this.confQuote, // Pass confQuote here
        (linesToAdd: ConfQuoteLine[]) => {
          if (linesToAdd) {
            this.productListService.replaceLineOrAddNew(
              this.confQuote,
              linesToAdd,
              line
            );
            this.addItemLine(null);
            this.refresh();
          }
        },
        this.allAttr
      );
    } else {
      line = this.productListService.fillProdLine(item, line);
      this.addItemLine(null);
    }
  }

  changeTotalDisc(totalDiscount: number) {
    this.confQuote.totalDiscount = totalDiscount;
    let emptyLine = this.confQuote.quotelines.find((x) => x.product == null);
    emptyLine.discount = this.confQuote.totalDiscount;
  }

  changeWarehouse(defaultWarehouseId: number) {
    this.selectedWarehouseId = defaultWarehouseId;
    let emptyLine = this.confQuote.quotelines.find((x) => x.product == null);
    emptyLine.warehouse_id = this.selectedWarehouseId;
  }

  private getProductFields() {
    this.entityService
      .getFldsEntityByName('product')
      .pipe(take(1))
      .subscribe((res) => {
        this.productFieldsForScanner = res.fields;
        this.product_fields = this.productListService.filterProductFields(
          res.fields
        );
        this.rememberSelectedCols();
      });
  }

  updateSelectedColumns($event: string[]) {
    this.selectedColumns = this.productListService.updateSelectedColumns(
      this.selectedColumns,
      $event
    );
    this.productListService.storeSelectedColumnsToLocalstorage(
      this.selectedColumns,
      this.localstorageKey
    );
  }

  // Code that is used in Karag Installation
  private checkForContainerRounding() {
    this.productListService.checkForContainerRounding(this.confQuote);
  }

  calculateQuantity(element: any) {
    element.containers = Math.ceil(element.containers);
    element.qty = parseFloat(
      (element.product.product_alternate_mu * element.containers).toFixed(2)
    );
  }

  reorderProducts() {
    let counter = 1;
    for (let line of this.confQuote.quotelines) {
      line.aa = counter;
      counter++;
    }
  }

  openQRScanner(line: ConfQuoteLine, objectForScan: string) {
    const dialogRef = this.dialog.open(QrcodeScannerComponent, {
      data: null,
      disableClose: true,
      height: '80%',
      width: '80%',
    });

    dialogRef.afterClosed().subscribe((code) => {
      if (code !== undefined) {
        if (objectForScan === 'product') {
          this.scanProductCode(line, code);
        } else {
          this.scanWarehouseCode(line, code);
        }
      }
    });
  }

  onExportToExcel() {
    if (this.ITEMID !== 'new') {
      this.productListService.exportProdListToExcel(
        this.selectedColumns,
        +this.ITEMID,
        this.ITEM
      );
    }
  }

  // Copies old local storage items to the new naming convention
  // TO BE DELETED after some time
  private copyOldLocalStorageItemsToNewOnes() {
    const desktopItem = localStorage.getItem('grid_prodlist_desktop');
    const newDesktopItemName = `grid_${this.ITEM.fld_code_ent}_desktop`;
    const newDesktopItem = localStorage.getItem(newDesktopItemName);
    if (desktopItem !== null && newDesktopItem === null) {
      localStorage.setItem(newDesktopItemName, desktopItem);
    }

    const mobileItem = localStorage.getItem('grid_prodlist_mobile');
    const newMobileItemName = `grid_${this.ITEM.fld_code_ent}_mobile`;
    const newMobileItem = localStorage.getItem(newMobileItemName);
    if (mobileItem !== null && newMobileItem === null) {
      localStorage.setItem(newMobileItemName, mobileItem);
    }
  }

  // Merges the old qr-scanner-search and qr-scanner-validator columns to a new one
  // called qr-scanner-product
  // TO BE DELETED after some time
  private refreshColumnsOnLocalStorage() {
    const savedColumns = localStorage.getItem(this.localstorageKey);
    if (savedColumns !== null) {
      const parsedColumns = JSON.parse(savedColumns);
      const qrCodeSearchIndex = parsedColumns.indexOf('qr-scanner-search');
      const qrCodeValidatorIndex = parsedColumns.indexOf(
        'qr-scanner-validator'
      );

      if (qrCodeSearchIndex !== -1) {
        parsedColumns[qrCodeSearchIndex] = 'qr-scanner-product';
      }
      if (qrCodeValidatorIndex !== -1) {
        parsedColumns.splice(qrCodeValidatorIndex, 1);
      }

      localStorage.setItem(this.localstorageKey, JSON.stringify(parsedColumns));
    }
  }

  private setLocalStorageKeyValue() {
    const additionalLocalStorageKey = this.isHandset() ? 'mobile' : 'desktop';
    this.localstorageKey = `grid_${this.ITEM.fld_code_ent}_${additionalLocalStorageKey}`;
  }

  private fillTableLines() {
    if (this.ITEMID !== 'new') {
      this.getSavedLines(+this.ITEMID);
    } else {
      this.addItemLine(null);
    }
  }

  private scanProductCode(line: ConfQuoteLine, productCode: any) {
    if (!line.product) {
      this.productListService
        .getProductFromScanner(productCode, this.productFieldsForScanner)
        .then((res) => {
          if (res) {
            this.fillProdlineItem(res, line);
          }
        });
    } else {
      this.productListService.validateProductWithScanner(productCode, line);
    }
  }

  private scanWarehouseCode(line: ConfQuoteLine, code: any) {
    this.confQuote.quotelines = this.productListService.setWarehouseForLine(
      line,
      code,
      this.confQuote.quotelines
    );
    this.refresh();
  }

  onOpenProductWarehouseStockPopUp(line: ConfQuoteLine) {
    this.productListService.openProductWarehouseStockPopUp(line);
  }
}
